import InfoIcon from "@mui/icons-material/Info";

interface RegexInstructionsProps {
  regexSearch: boolean;
  isApiStartedCalling: boolean;
  invalidRegex?: boolean;
  searchText?: string;
}

const RegexInstructions: React.FC<RegexInstructionsProps> = ({
  regexSearch,
  isApiStartedCalling,
  invalidRegex = false,
  searchText = "",
}) => {
  return (
    regexSearch &&
    !isApiStartedCalling && (
      <div className="regex-invalid-container">
        {invalidRegex && (
          <p className="info-note">
            <span className="info-icon">
              <InfoIcon fontSize="small" />
            </span>
            <span>
              The Regex query is incorrect. Please ensure it conforms to the
              valid Regex input structure.
            </span>
          </p>
        )}
        {(searchText.length < 3 || (searchText.length > 0 && invalidRegex)) && (
          <div className="instruction">
            <p className="instruction-title">Instructions to search in Regex</p>
            <div className="instructions-list">
              <ul className="scroll">
                <li>
                  The operator "." matches any character.
                  <p>
                    <b>For example:</b> ab. matches:-'aba', 'abb', 'abz', etc.{" "}
                  </p>
                </li>
                <li>
                  The operator "?" repeats the preceding character zero or one
                  times. Often used to make the preceding character optional.
                  <p>
                    <b>For example:</b> abc? matches 'ab' and 'abc'.
                  </p>
                </li>
                <li>
                  The operator "+" repeats the preceding character one or more
                  times.
                  <p>
                    <b>For example:</b> ab+ matches 'ab', 'abb', 'abbb', etc.
                  </p>
                </li>
                <li>
                  The operator "*" repeats the preceding character zero or more
                  times.
                  <p>
                    <b>For example:</b> ab* matches 'a', 'ab', 'abb', 'abbb',
                    etc.
                  </p>
                </li>
                <li>
                  The operator "
                  <code>
                    {"{"}
                    {"}"}
                  </code>
                  " specifies the minimum and maximum number of times the
                  preceding character can repeat.
                  <p>
                    <b>For example:</b>{" "}
                    <code>
                      {" "}
                      a{"{"}2{"}"}
                    </code>{" "}
                    matches 'aa'.
                  </p>
                  <p>
                    <b>For example:</b>{" "}
                    <code>
                      {" "}
                      a{"{"}2,{"}"}
                    </code>
                    ,{" "}
                    <code>
                      {" "}
                      a{"{"}2,4{"}"}
                    </code>{" "}
                    matches 'aa', 'aaa', and 'aaaa'.
                  </p>
                  <p>
                    <b>For example:</b>{" "}
                    <code>
                      {" "}
                      a{"{"}2,{"}"}
                    </code>{" "}
                    matches 'a' repeated two or more times.
                  </p>
                </li>
                <li>
                  The operator "|" OR operator. The match will succeed if the
                  longest pattern on either the left side OR the right-side
                  matches.
                  <p>
                    <b>For example:</b> abc|xyz matches 'abc' and 'xyz'.
                  </p>
                </li>
                <li>
                  The operator "( … )" forms a group. You can use a group to
                  treat part of the expression as a single character.
                  <p>
                    <b>For example:</b> abc(def)? matches 'abc' and 'abcdef' but
                    not 'abcd'.
                  </p>
                </li>
                <li>
                  The operator "[ … ]" match one of the characters in the
                  brackets.
                  <p>
                    <b>For example:</b> [abc] matches 'a', 'b', 'c'.
                  </p>
                  <p>
                    <b>For example:</b> [a-c] matches 'a', 'b', or 'c'.
                  </p>
                  <p>
                    <b>For example:</b> [-abc] Here, '-' is first characte
                    matches '-', 'a', 'b', or 'c'.
                  </p>
                  <p>
                    <b>For example:</b> [abc\-] Here, Escapes '-' matches 'a',
                    'b', 'c', or '-'.
                  </p>
                </li>
                <li>
                  The operator "^" matches before a character in the brackets
                  negates the character or range.
                  <p>
                    <b>For example:</b> [^abc] matches any character except 'a',
                    'b', or 'c'.
                  </p>
                  <p>
                    <b>For example:</b> [^a-c] matches any character except 'a',
                    'b', or 'c'.
                  </p>
                  <p>
                    <b>For example:</b> [^-abc] matches any character except
                    '-', 'a', 'b', or 'c'.
                  </p>
                  <p>
                    <b>For example:</b> [^abc\-] matches any character except
                    'a', 'b', 'c', or '-'.
                  </p>
                </li>
                <li>
                  Matching Multiple Words: To match multiple words, you can use
                  the pipe "|" symbol to indicate alternatives.
                  <p>
                    <b>For example:</b> To match either "apple" or "banana", you
                    can use: apple|banana.
                  </p>
                </li>
                <li>
                  You can use ~ to negate the shortest following pattern.
                  <p>
                    <b>For example:</b> a~bc will match 'adc' and 'aec' but not
                    'abc'.
                  </p>
                </li>
                <li>
                  The # operator doesn't match any string, not even an empty
                  string.
                  <p>
                    <b>For example:</b> #|abc will match 'abc' but nothing else,
                    not even an empty string.
                  </p>
                </li>
                <li>
                  The {"<>"} INTERVAL operators. You can use {"<>"} to match a
                  numeric range.
                  <p>
                    <b>For example:</b> foo{"<1-100>"} matches 'foo1', 'foo2'
                    ... 'foo99', 'foo100'.{" "}
                  </p>
                  <p>
                    <b>For example:</b> foo{"<01-100>"} matches 'foo01', 'foo02'
                    ... 'foo99', 'foo100'.
                  </p>
                </li>
                <li>
                  The "&" INTERSECTION operator, which acts as an AND operator.
                  The match will succeed if patterns on both the left side AND
                  the right-side matches.
                  <p>
                    <b>For example:</b> aaa.+&.+bbb will match 'aaabbb'.
                  </p>
                </li>
                <li>
                  The "@" ANYSTRING operator. You can use @ to match any entire
                  string. You can combine the @ operator with & and ~ operators
                  to create an "everything except" logic.
                  <p>
                    <b>For example:</b> @&~(abc.+) will match everything except
                    terms beginning with 'abc'.
                  </p>
                </li>
                <li>"NONE" Disables all optional operators.</li>
                <li>
                  Unsupported operators: Our regular expression engine does not
                  support anchor operators, such as ^ (beginning of line) or $
                  (end of line). To match a term, the regular expression must
                  match the entire string.
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    )
  );
};

export default RegexInstructions;
